// The React-owned module shell (M1 Phase 4, PR 9 — the shell cutover).
//
// Renders one `.placed-module` from its store entry, emitting the SAME DOM the
// legacy `createModule` built as an innerHTML string (header + mobile-drag-handle
// + title + pin/close + optional `.module-sub-controls` + `.module-body` +
// `.resize-handle`), so the shared CSS + the visual regression baselines are
// unchanged. Geometry (left/top/width/height) is derived from the stored grid
// coords — the store is the source of truth (Phase 2).
//
// Desktop only: `<Workspace>` renders these only on desktop; the mobile stack
// stays the legacy `createModule` path until PR 10.
//
// Wrapped in `React.memo` so that a store change to one module doesn't re-render
// the others — important during a drag, where the legacy engine mutates every
// module's inline `style` directly and a stray re-render would clobber the
// in-progress geometry.

import { memo, useEffect, useRef, useState, type ReactNode } from "react";
import { gridPosToPx, gridSizeToPx } from "../lib/grid.js";
import { useBentoStore } from "../store/index.js";
import type { BentoModule, ModuleControls as ModuleControlsState } from "../store/module.js";
import { hasReactControls } from "./moduleControlsConfig.js";
import ModuleControls from "./ModuleControls.js";
import ModuleBody from "./ModuleBody.js";
import MovementHistory from "./MovementHistory.js";
import { startModuleHold, startModuleResize } from "../mobile/engine.js";
import type { Rect } from "../mobile/layout.js";
import { hasMovementHistory } from "./movementHistoryConfig.js";

// The 8 resize handles: one per corner + edge. The bottom-right one keeps the
// original `.resize-handle` class (mobile hide rule, cursor rule); the rest are
// `.resize-handle-{dir}`. Each draws the same ">" glyph, rotated outward in CSS.
const RESIZE_DIRS = ["se", "nw", "ne", "sw", "n", "s", "e", "w"] as const;

// The Market & Odds modules carry `.betting-module` (their navy look). Their
// widgets also add it imperatively on mount, but any re-render of this shell —
// e.g. the hover that shows the resize handles — rebuilds `className` and wipes
// a class added outside React (the widgets don't re-add it), so the shell owns it.
const BETTING_TITLES = new Set([
  "Winner (1x2)",
  "Winner (1x2) - Movement",
  "Match Goals",
  "Match Goals - Movement",
  "Player Goals",
  "Player Goals - Movement",
  "Player Cards",
  "Player Cards - Movement",
  "Match Cards",
  "Match Cards - Movement",
  "Value Indicator",
  "Margin Calculator",
]);

function PlacedModule({
  module: m,
  mobileRect = null,
}: {
  module: BentoModule;
  /** Phone: where the layout places this module (null on desktop). */
  mobileRect?: Rect | null;
}) {
  const { id, title, col, row, cols, rows, pinned } = m;
  const rootRef = useRef<HTMLDivElement>(null);
  // Only the two modules whose flag flips re-render on a hover change.
  const resizeActive = useBentoStore((s) => s.resizeActiveId === id);
  const selected = useBentoStore((s) => s.selectedIds.includes(id));
  const bulkClosing = useBentoStore((s) => s.closingIds.includes(id));
  // Pin press replays a one-shot animation (legacy toggled a `pin-animating`
  // class with a forced reflow); in React it's local UI state cleared on
  // animation end. Close plays the exit animation before the store removal
  // unmounts us (removing from the store first would unmount with no animation).
  const [pinAnimating, setPinAnimating] = useState(false);
  const [closeClicked, setClosing] = useState(false);
  // A module closes either from its own × or as part of a bulk (marquee) delete.
  const closing = closeClicked || bulkClosing;
  // Movement modules flip (header clock icon) to a Movement History back face.
  const hasHistory = hasMovementHistory(title);
  const [flipped, setFlipped] = useState(false);
  const liveFixture = useBentoStore((s) => s.fixture);

  // Wire the legacy overflow behaviour (hide sub-controls that don't fit a narrow
  // module) onto the React-rendered controls, once the shell + controls are in the
  // DOM. It installs its own ResizeObserver on the bar. Runs once per mount.
  const controlsWired = useRef(false);
  useEffect(() => {
    if (controlsWired.current || !hasReactControls(title)) return;
    controlsWired.current = true;
    if (rootRef.current) window.bentoShell?.watchControlsOverflow?.(rootRef.current);
  }); // eslint has no exhaustive-deps rule here; the ref guard makes it run once

  const slug = window.ModuleLoader?.getSlug(title) ?? "";
  const className =
    "placed-module" +
    (slug ? ` module-${slug}` : "") +
    (BETTING_TITLES.has(title) ? " betting-module" : "") +
    (closing ? " module-closing" : "") +
    (resizeActive ? " resize-active" : "") +
    (selected ? " module-selected" : "") +
    (flipped ? " history-flipped" : "");

  // Desktop: from the grid coords. Phone: from the phone layout (px).
  const style = mobileRect
    ? { left: mobileRect.x, top: mobileRect.y, width: mobileRect.w, height: mobileRect.h }
    : {
        left: gridPosToPx(col),
        top: gridPosToPx(row),
        width: gridSizeToPx(cols),
        height: gridSizeToPx(rows),
      };

  function togglePin() {
    window.bentoShell.saveState();
    const next = !pinned;
    useBentoStore.getState().setModulePinned(id, next);
    if (next) setPinAnimating(true); // replay the pin animation only on pinning
  }

  function onClose() {
    window.bentoShell.saveState();
    setClosing(true); // play the exit animation; removeModule fires on its end
  }

  // The module header. A flipping module renders two (front + back), so both
  // carry the drag wiring and the pin/close actions; `extra` is the flip button.
  function header(text: string, extra?: ReactNode) {
    return (
      <div
        className="module-header"
        onPointerDown={(e) =>
          mobileRect
            ? startModuleHold(rootRef.current!, e.nativeEvent)
            : window.bentoShell.startModuleGesture(rootRef.current!, e.nativeEvent)
        }
      >
        <div className="mobile-drag-handle" title="Drag to reorder">
          <svg width="10" height="14" viewBox="0 0 10 14" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
            <circle cx="2" cy="2" r="1.5" /><circle cx="8" cy="2" r="1.5" />
            <circle cx="2" cy="7" r="1.5" /><circle cx="8" cy="7" r="1.5" />
            <circle cx="2" cy="12" r="1.5" /><circle cx="8" cy="12" r="1.5" />
          </svg>
        </div>
        <div className="module-title">{text}</div>
        <div className="header-actions">
          <div
            className={
              "module-pin" + (pinned ? " pinned" : "") + (pinAnimating ? " pin-animating" : "")
            }
            title="Pin this module"
            onClick={togglePin}
            onAnimationEnd={() => setPinAnimating(false)}
          >
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="17" x2="12" y2="22"></line><path d="M5 17h14v-1.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.68V6a3 3 0 1 0-6 0v4.68a2 2 0 0 1-1.11 1.87l-1.78.9A2 2 0 0 0 5 15.24Z"></path></svg>
          </div>
          {extra}
          <div className="module-close" onClick={onClose}>×</div>
        </div>
      </div>
    );
  }

  const front = (
    <>
      {header(
        title,
        hasHistory && (
          <div
            className="module-history"
            title="Movement history"
            onPointerDown={(e) => e.stopPropagation()}
            onClick={() => setFlipped(true)}
          >
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9"></circle><polyline points="12 7 12 12 15 13.5"></polyline></svg>
          </div>
        )
      )}
      {hasReactControls(title) && (
        <div className="module-sub-controls">
          <ModuleControls
            moduleId={id}
            title={title}
            saveState={() => window.bentoShell.saveState()}
            refreshBody={() => {}}
            onControlChange={(field: keyof ModuleControlsState, value: string) =>
              window.bentoShell.syncControlAcrossModules(id, field, value)
            }
          />
        </div>
      )}
      <div className="module-body">
        <ModuleBody moduleId={id} title={title} />
      </div>
    </>
  );

  return (
    <div
      ref={rootRef}
      className={className}
      data-module-id={id}
      data-react-shell="1"
      // Legacy code branches on `data-bento-controls` to read control values from
      // the store rather than DOM <select>s (e.g. the fixture-change team reset).
      // React-controls modules keep carrying it so those branches still fire.
      {...(hasReactControls(title) ? { "data-bento-controls": "1" } : {})}
      data-base-cols={cols}
      data-base-rows={rows}
      data-current-multiplier="1"
      data-pinned={pinned ? "true" : "false"}
      style={style}
      onAnimationEnd={(e) => {
        // Only the module's own exit animation removes it (child animations
        // bubble an animationend too).
        if (closing && e.target === e.currentTarget) {
          useBentoStore.getState().removeModule(id);
        }
      }}
    >
      {hasHistory ? (
        <div className="module-flip-outer">
          <div className="module-flip-inner">
            <div className="module-flip-front">{front}</div>
            <div className="module-flip-back">
              {header(
                "Movement History",
                <div
                  className="module-history-back"
                  title={`Back to ${title}`}
                  onPointerDown={(e) => e.stopPropagation()}
                  onClick={() => setFlipped(false)}
                >
                  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="19" y1="12" x2="5" y2="12"></line><polyline points="12 19 5 12 12 5"></polyline></svg>
                </div>
              )}
              <MovementHistory title={title} fixture={liveFixture} controls={m.controls} />
            </div>
          </div>
        </div>
      ) : (
        front
      )}
      {/* Phone: drag to change the height (1.25–3.75 half-widths). Hidden on desktop. */}
      <div
        className="mobile-resize-handle"
        title="Drag to resize"
        onPointerDown={(e) => startModuleResize(rootRef.current!, e.nativeEvent)}
      />
      {RESIZE_DIRS.map((dir) => (
        <div
          key={dir}
          className={dir === "se" ? "resize-handle" : `resize-handle-${dir}`}
          data-resize-dir={dir}
          onPointerDown={(e) =>
            window.bentoShell.startModuleResize(rootRef.current!, e.nativeEvent, dir)
          }
        >
          <svg viewBox="0 0 24 24" width="10" height="10" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 6 15 12 9 18"></polyline></svg>
        </div>
      ))}
    </div>
  );
}

export default memo(PlacedModule);
